import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, ClipboardList, Package, LogOut, Factory, Truck,
  Receipt, BarChart3, Menu, X, Wrench, Shirt, Users2, Sparkles, Send,
  ShoppingCart, FileText, Sun, Moon, Cog, Wallet, ScrollText, ShoppingBag,
  Settings as SettingsIcon, User, ListTodo, ChevronDown, ChevronRight,
  Briefcase, KeyRound, RefreshCw, MessageCircle, Fingerprint, CalendarClock, Camera, Clock,
  BookOpen, ShieldAlert, Warehouse, Boxes, UserPlus, PackageCheck, Store,
  Percent,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import MobileQuickActions from "@/components/MobileQuickActions";
import CopilotWidget from "@/components/CopilotWidget";
import AiProductDraftDialog from "@/components/AiProductDraftDialog";
import NotificationBell from "@/components/NotificationBell";
import { HelpButton, WhatsNewBanner } from "@/pages/LearningCenter";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import AccountInfoDialog from "@/components/AccountInfoDialog";
import { ensureFaceModels } from "@/lib/faceRecognition";
import useIdleLogout, { isMobileUserAgent, clearIdleActivityTracking } from "@/hooks/useIdleLogout";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { UserCircle2 } from "lucide-react";

// Menü grupları — kullanıcı isteğiyle düzenlendi (10 kategori, renkli başlıklar)
const NAV_GROUPS = [
  {
    key: "dashboard", label: "Kontrol Paneli", icon: LayoutDashboard, single: true,
    color: { text: "text-rose-400", ring: "ring-rose-500/40", glow: "shadow-[0_0_18px_-2px_rgba(244,63,94,0.55)]" },
    items: [
      { to: "/", label: "Kontrol Paneli", icon: LayoutDashboard, exact: true, testId: "nav-dashboard", module: "dashboard" },
    ],
  },
  {
    key: "sales", label: "Satış & Pazarlama", icon: ShoppingCart, badge: "yeni",
    color: { text: "text-sky-400", ring: "ring-sky-500/40", glow: "shadow-[0_0_18px_-2px_rgba(56,189,248,0.55)]" },
    items: [
      { to: "/siparis-sihirbazi?mode=quote", label: "Teklif Sihirbazı", icon: FileText, testId: "nav-quote-wizard", module: "wizard" },
      { to: "/siparis-sihirbazi", label: "Sipariş Sihirbazı", icon: Sparkles, testId: "nav-order-wizard", badge: "yeni", module: "wizard" },
      { to: "/teklifler", label: "Teklifler", icon: FileText, testId: "nav-quotes", module: "quotes" },
      { to: "/satislar", label: "Satışlar", icon: Receipt, testId: "nav-sales", module: "orders" },
      { to: "/musteri", label: "Müşteriler", icon: Users, testId: "nav-customers", module: "customers" },
    ],
  },
  {
    key: "store", label: "Mağaza", icon: Store, badge: "yeni",
    color: { text: "text-lime-400", ring: "ring-lime-500/40", glow: "shadow-[0_0_18px_-2px_rgba(163,230,53,0.55)]" },
    items: [
      { to: "/magaza/hizli-satis", label: "Hızlı Satış", icon: Store, testId: "nav-quick-sale", badge: "yeni", module: "orders" },
    ],
  },
  {
    key: "purchasing", label: "Satın Alma & Tedarik", icon: ShoppingBag, badge: "yeni",
    color: { text: "text-emerald-400", ring: "ring-emerald-500/40", glow: "shadow-[0_0_18px_-2px_rgba(52,211,153,0.55)]" },
    items: [
      { to: "/satin-alma-ihtiyaclari", label: "Satın Alma İhtiyaçları", icon: ShoppingBag, testId: "nav-purchase-req", badge: "yeni", module: "purchase_requirements" },
      { to: "/satinalma", label: "Alışlar", icon: Receipt, testId: "nav-purchases", module: "purchases" },
      { to: "/fatura-kontrol", label: "Fatura Kontrol", icon: ScrollText, testId: "nav-invoice-check", badge: "yeni", module: "purchase_requirements" },
      { to: "/tedarikci", label: "Tedarikçiler", icon: Truck, testId: "nav-suppliers", module: "suppliers" },
    ],
  },
  {
    key: "inventory", label: "Ürün & Stok Yönetimi", icon: Package,
    color: { text: "text-teal-400", ring: "ring-teal-500/40", glow: "shadow-[0_0_18px_-2px_rgba(45,212,191,0.55)]" },
    items: [
      { to: "/urunler", label: "Ürünler", icon: Shirt, testId: "nav-products", module: "products" },
      { to: "/varyantlar", label: "Varyantlar", icon: Sparkles, testId: "nav-variants", module: "products" },
      { to: "/stok", label: "Stok Yönetimi", icon: Package, testId: "nav-inventory", module: "warehouse" },
      { to: "/depolar", label: "Depolar", icon: Warehouse, testId: "nav-warehouses", module: "warehouse" },
      { to: "/sevkiyat", label: "Sevkiyat", icon: Send, testId: "nav-shipments", module: "shipments" },
      { to: "/mal-kabul", label: "Mal Kabul", icon: PackageCheck, testId: "nav-goods-receipt", badge: "yeni", module: "warehouse" },
      { to: "/numuneler", label: "Numune Takip", icon: Boxes, testId: "nav-samples", badge: "yeni", module: "samples" },
    ],
  },
  {
    key: "production", label: "Üretim & Operasyon", icon: Factory, badge: "yeni",
    color: { text: "text-blue-400", ring: "ring-blue-500/40", glow: "shadow-[0_0_18px_-2px_rgba(251,146,60,0.60)]" },
    items: [
      { to: "/uretim", label: "Üretim Takibi", icon: ClipboardList, testId: "nav-production", module: "production" },
      { to: "/operasyonlar", label: "Operasyonlar", icon: Cog, testId: "nav-operations", badge: "yeni", module: "operations" },
    ],
  },
  {
    key: "finance", label: "Finans & Muhasebe", icon: Briefcase, badge: "yeni",
    color: { text: "text-amber-400", ring: "ring-amber-500/40", glow: "shadow-[0_0_18px_-2px_rgba(251,191,36,0.55)]" },
    items: [
      { to: "/cari", label: "Cari Hesap", icon: Wallet, testId: "nav-cari", badge: "yeni", module: "finance" },
      { to: "/cek-senet", label: "Çek / Senet", icon: ScrollText, testId: "nav-checks", badge: "yeni", module: "checks" },
    ],
  },
  {
    key: "hr", label: "İnsan Kaynakları & Organizasyon", icon: Users2,
    color: { text: "text-pink-400", ring: "ring-pink-500/40", glow: "shadow-[0_0_18px_-2px_rgba(244,114,182,0.55)]" },
    items: [
      { to: "/departmanlar", label: "Departmanlar", icon: Wrench, testId: "nav-departments", module: "departments" },
      { to: "/calisan", label: "Çalışanlar", icon: Users2, testId: "nav-employees", module: "employees" },
      { to: "/ise-alim", label: "İşe Alım", icon: UserPlus, testId: "nav-candidates", badge: "yeni", module: "employees" },
      { to: "/pdks/panel", label: "PDKS Paneli", icon: Fingerprint, testId: "nav-pdks-panel", badge: "yeni", module: "pdks" },
      { to: "/pdks/terminal", label: "PDKS Terminali", icon: Camera, testId: "nav-pdks-kiosk", module: "pdks", external: true, badge: "kiosk" },
      { to: "/pdks/hareketler", label: "Günlük Hareketler", icon: CalendarClock, testId: "nav-pdks-movements", module: "pdks" },
      { to: "/pdks/puantaj", label: "Puantaj (Günlük)", icon: ClipboardList, testId: "nav-pdks-timesheet", module: "pdks" },
      { to: "/pdks/fazla-mesai", label: "Fazla Mesai", icon: Clock, testId: "nav-pdks-overtime", module: "pdks" },
      { to: "/pdks/ayarlar", label: "PDKS Ayarları", icon: Cog, testId: "nav-pdks-settings", module: "pdks" },
      { to: "/puantaj/aylik", label: "Aylık Puantaj", icon: ClipboardList, testId: "nav-payroll-timesheet", badge: "yeni", module: "puantaj" },
      { to: "/maas/hesaplama", label: "Maaş Hesaplama", icon: Wallet, testId: "nav-payroll-salary", badge: "yeni", module: "maas" },
      { to: "/prim-komisyon", label: "Prim / Komisyon", icon: Percent, testId: "nav-commissions", badge: "yeni", module: "maas" },
      { to: "/portal", label: "Portalım", icon: User, testId: "nav-portal", module: null },
    ],
  },
  {
    key: "tasks", label: "Görev & İş Yönetimi", icon: ListTodo, badge: "yeni",
    color: { text: "text-violet-400", ring: "ring-violet-500/40", glow: "shadow-[0_0_18px_-2px_rgba(167,139,250,0.55)]" },
    items: [
      { to: "/gorevler", label: "Görevler", icon: ListTodo, testId: "nav-tasks", badge: "yeni", module: null },
    ],
  },
  {
    key: "reports", label: "Raporlar", icon: BarChart3, single: true,
    color: { text: "text-purple-400", ring: "ring-purple-500/40", glow: "shadow-[0_0_18px_-2px_rgba(192,132,252,0.55)]" },
    items: [
      { to: "/raporlar", label: "Raporlar", icon: BarChart3, exact: false, testId: "nav-reports", module: "reports" },
    ],
  },
  {
    key: "whatsapp", label: "WhatsApp Sohbetleri", icon: MessageCircle, single: true, badge: "yeni",
    items: [
      { to: "/whatsapp", label: "WhatsApp Sohbetleri", icon: MessageCircle, testId: "nav-whatsapp", module: null },
    ],
  },
  {
    key: "learning", label: "Eğitim & Yardım", icon: BookOpen, single: true, badge: "yeni",
    color: { text: "text-blue-300", ring: "ring-blue-500/40", glow: "shadow-[0_0_18px_-2px_rgba(251,146,60,0.55)]" },
    items: [
      { to: "/egitim", label: "Eğitim Merkezi", icon: BookOpen, testId: "nav-learning", module: null },
      { to: "/hesabim/krediler", label: "Kredilerim", icon: KeyRound, testId: "nav-credits", module: null },
    ],
  },
  {
    key: "system", label: "Sistem & Ayarlar", icon: SettingsIcon,
    color: { text: "text-slate-300", ring: "ring-slate-500/40", glow: "shadow-[0_0_18px_-2px_rgba(148,163,184,0.45)]" },
    items: [
      { to: "/ayarlar", label: "Ayarlar", icon: SettingsIcon, testId: "nav-settings", module: "settings", altModules: ["audit"] },
      { to: "/ayarlar/entegrasyonlar/muhasebe", label: "Muhasebe Entegrasyonu", icon: SettingsIcon, testId: "nav-accounting-hub", module: "settings" },
    ],
    hasThemeToggle: true,
  },
  {
    key: "super-admin", label: "Süper Admin", icon: ShieldAlert, superAdminOnly: true,
    color: { text: "text-blue-300", ring: "ring-blue-500/40", glow: "shadow-[0_0_18px_-2px_rgba(251,146,60,0.55)]" },
    items: [
      { to: "/super-admin", label: "Şirket Yönetimi", icon: ShieldAlert, testId: "nav-super-admin", module: null },
      { to: "/super-admin/veri-migrasyonu", label: "Veri Migrasyonu", icon: ShieldAlert, testId: "nav-data-migration", module: null },
      { to: "/super-admin/secici-kurtarma", label: "Seçici Kurtarma", icon: ShieldAlert, testId: "nav-selective-restore", module: null },
    ],
  },
];

// FAZ B UI — Sidebar bölüm başlıkları (Operix referans görseli uyumu)
// Her group.key hangi üst bölüme ait olduğunu belirler; renderer bölüm
// değiştiğinde küçük başlık + ince ayırıcı çizgi çizer.
const GROUP_SECTIONS = {
  dashboard: "ANA",
  sales: "TİCARİ",
  purchasing: "TİCARİ",
  inventory: "STOK & ÜRETİM",
  production: "STOK & ÜRETİM",
  finance: "FİNANS",
  hr: "YÖNETİM",
  tasks: "YÖNETİM",
  reports: "YÖNETİM",
  whatsapp: "İLETİŞİM",
  learning: "İLETİŞİM",
  system: "SİSTEM",
  "super-admin": "SİSTEM",
};

// Accordion invariant: aynı anda EN FAZLA 1 ana grup açık.
// Eski localStorage kalıntılarını normalize eder; geçersiz anahtarları atar.
const NAV_GROUP_KEYS = new Set(NAV_GROUPS.map((g) => g.key));
const normalizeOpenGroups = (saved, activeKey) => {
  const openKeys = Object.keys(saved || {}).filter((k) => saved[k] && NAV_GROUP_KEYS.has(k));
  if (openKeys.length === 0) return {};
  if (openKeys.length === 1) return { [openKeys[0]]: true };
  if (activeKey && saved[activeKey]) return { [activeKey]: true };
  return { [openKeys[0]]: true };
};

export default function AppLayout() {
  const { user, logout, can, refresh } = useAuth();

  // PDKS yüz tanıma modellerini oturum açar açmaz arka planda ısıt —
  // SADECE `pdks` modülüne yetkisi olan kullanıcılar için (yaklaşık 7MB).
  // Yetkisi olmayanlar için gereksiz bant genişliği + `/models/*.bin` fetch
  // hatalarını engeller (bkz. M9 E2E raporu #2).
  useEffect(() => {
    if (!user) return;
    if (!can("pdks", "view")) return;
    ensureFaceModels().catch(() => { /* sessizce geç */ });
  }, [user, can]);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [aiProductOpen, setAiProductOpen] = useState(false);
  const [aiProductPrompt, setAiProductPrompt] = useState("");

  // Global event: `fucan:open-ai-product` — Copilot Widget veya başka bileşenler
  // tarafından tetiklenir. Detail.prompt kısmını başlangıç prompt'u yapar.
  useEffect(() => {
    const handler = (e) => {
      setAiProductPrompt(e?.detail?.prompt || "");
      setAiProductOpen(true);
    };
    window.addEventListener("fucan:open-ai-product", handler);
    return () => window.removeEventListener("fucan:open-ai-product", handler);
  }, []);

  // İlk girişte force_password_change true ise otomatik aç
  useEffect(() => {
    if (user?.force_password_change) {
      setChangePwOpen(true);
    }
  }, [user?.force_password_change]);

  const visibleGroups = useMemo(() => {
    return NAV_GROUPS
      .filter((g) => !g.superAdminOnly || user?.is_super_admin)
      .map((g) => ({
        ...g,
        items: g.items.filter((i) => !i.module || can(i.module, "view") || (i.altModules || []).some((m) => can(m, "view"))),
      }))
      .filter((g) => g.items.length > 0);
  }, [can, user?.is_super_admin]);
  const { theme, toggle } = useTheme();

  // Aktif rota'ya göre hangi grup açık?
  const activeGroupKey = useMemo(() => {
    for (const g of visibleGroups) {
      for (const it of g.items) {
        if (it.exact ? location.pathname === it.to : location.pathname.startsWith(it.to)) {
          return g.key;
        }
      }
    }
    return null;
  }, [visibleGroups, location.pathname]);

  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("fucan_nav_groups") || "{}");
      const normalized = normalizeOpenGroups(saved, activeGroupKey);
      localStorage.setItem("fucan_nav_groups", JSON.stringify(normalized));
      return normalized;
    } catch { return {}; }
  });

  // Overlay panel mimarisi: submenu document flow'da YÜKSEKLIK OLUŞTURMAZ —
  // ana menü blokları asla aşağı/yukarı kaymaz. Route değişiminde açık grup
  // kapanır (önceki grup asla açık kalmaz). Invariant: açık grup <= 1.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;
    setOpenGroups((prev) => {
      if (!Object.keys(prev).length) return prev;
      try { localStorage.setItem("fucan_nav_groups", "{}"); } catch { /* ignore */ }
      return {};
    });
  }, [location.pathname]);

  const groupBtnRefs = useRef({});
  const mobileBtnRefs = useRef({});
  const closeGroups = useCallback(() => {
    setOpenGroups((prev) => {
      if (!Object.keys(prev).length) return prev;
      try { localStorage.setItem("fucan_nav_groups", "{}"); } catch { /* ignore */ }
      return {};
    });
  }, []);

  // Açık olan (paneli görünen) grup — single gruplar panel kullanmaz
  const openGroup = visibleGroups.find((g) => !g.single && openGroups[g.key]) || null;

  // Panel açıkken resize / Escape / dışarı dokunma ile kapat.
  // Görünmez backdrop KULLANILMAZ — böylece diğer grup butonları tek
  // dokunuşla doğrudan seçilebilir (görünmez katman yutmaz).
  useEffect(() => {
    if (!openGroup) return;
    const onResize = () => closeGroups();
    const onKey = (e) => { if (e.key === "Escape") closeGroups(); };
    const onDown = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest("[data-testid^='nav-group-']")) return; // panel + grup butonları
      closeGroups();
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [openGroup, closeGroups]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      // Accordion: sadece bir grup açık kalabilir. Aynı gruba tıklarsa kapansın.
      const next = prev[key] ? {} : { [key]: true };
      try { localStorage.setItem("fucan_nav_groups", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
    toast.success("Çıkış yapıldı");
    // Manuel çıkışta idle tracking anahtarını temizle — bir sonraki oturum
    // taze başlasın (eski ts ile yanlış tetikleme olmasın).
    clearIdleActivityTracking();
    // Prevent back-button re-entry: full replace + clear history state
    try { window.history.replaceState(null, "", "/login"); } catch { /* ignore */ }
    window.location.replace("/login");
  };

  // OPERIX Oturum Güvenliği — WEB / MASAÜSTÜ:
  //   2 saat hareketsizlik → otomatik logout + Türkçe güvenlik mesajı.
  //   MOBİL kullanıcı ajanı ise devreye alınmaz (mobilde oturum kalıcı).
  //   Yalnız `user` object (giriş yapmış) iken çalışır.
  useIdleLogout({
    enabled: !!user && typeof user === "object" && !isMobileUserAgent(),
    timeoutMs: 2 * 60 * 60 * 1000, // 2 saat
    onIdle: async () => {
      try { await logout(); } catch { /* ignore */ }
      clearIdleActivityTracking();
      // Login sayfası mount'unda gösterilmek üzere mesajı sessionStorage'a bırak
      // — toast doğrudan burada gösterilmesi window.location.replace ile hemen
      // kaybolurdu.
      try {
        sessionStorage.setItem(
          "operix:logoutReason",
          "Güvenlik nedeniyle 2 saat işlem yapılmadığı için oturumunuz kapatıldı."
        );
      } catch { /* ignore */ }
      try { window.history.replaceState(null, "", "/login"); } catch { /* ignore */ }
      window.location.replace("/login");
    },
  });

  const forceUpdate = async () => {
    const t = toast.loading("Uygulama güncelleniyor...");
    try {
      // 1) Tüm cache'leri sil
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // 2) Service worker'ları unregister et
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (_) { /* ignore */ }
    toast.dismiss(t);
    // 3) Hard reload — cache bypass için query string ekle
    const sep = window.location.href.includes("?") ? "&" : "?";
    window.location.href = window.location.href.split("#")[0] + sep + "_v=" + Date.now();
  };

  const currentLabel = useMemo(() => {
    for (const g of visibleGroups) {
      for (const it of g.items) {
        if (it.exact ? location.pathname === it.to : location.pathname.startsWith(it.to)) {
          return it.label;
        }
      }
    }
    return "ACUTO 360";
  }, [visibleGroups, location.pathname]);

  const SidebarContent = ({ onNavigate, btnRefs = groupBtnRefs }) => {
    // Submenu öğeleri — overlay panel içinde render edilir (document flow DIŞI)
    const renderGroupItems = (group) => (
      <>
        {group.items.map((item) => {
          const ItemIcon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              data-testid={item.testId}
              onClick={() => { closeGroups(); if (onNavigate) onNavigate(); }}
              className={({ isActive }) => {
                // Aynı path'i paylaşan sihirbaz girişleri: aktiflik query ile ayrışır
                if (item.testId === "nav-quote-wizard") isActive = location.pathname === "/siparis-sihirbazi" && location.search.includes("mode=quote");
                if (item.testId === "nav-order-wizard") isActive = location.pathname === "/siparis-sihirbazi" && !location.search.includes("mode=quote");
                return `flex items-center gap-2 pl-8 pr-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-blue-600 text-white font-semibold"
                    : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"
                }`;
              }}
            >
              <ItemIcon className="w-3.5 h-3.5" strokeWidth={2} />
              <span className="flex-1">{item.label}</span>
              {item.badge && (
                <span className="text-[9px] uppercase tracking-widest font-bold bg-emerald-500 text-emerald-950 px-1.5 py-0.5 rounded-sm">
                  {item.badge}
                </span>
              )}
            </NavLink>
          );
        })}
        {group.hasThemeToggle && (
          <button
            type="button"
            data-testid="theme-toggle-btn"
            onClick={toggle}
            className="w-full flex items-center gap-2 pl-8 pr-3 py-2 rounded-sm text-sm text-slate-400 hover:bg-slate-800/60 hover:text-slate-100 transition-colors"
            title="Tema değiştir"
          >
            {theme === "dark"
              ? <Sun className="w-3.5 h-3.5 text-amber-400" />
              : <Moon className="w-3.5 h-3.5 text-sky-400" />}
            <span className="flex-1 text-left">
              {theme === "dark" ? "Aydınlık Tema" : "Karanlık Tema"}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-blue-400">
              {theme === "dark" ? "Aç" : "Kapat"}
            </span>
          </button>
        )}
      </>
    );
    return (
    <>
      <div className="px-5 py-6 border-b border-slate-800 flex items-center justify-between">
        <NavLink
          to="/"
          data-testid="sidebar-home-link"
          onClick={onNavigate}
          className="flex flex-col items-start gap-1.5 hover:opacity-80 transition-opacity w-full"
          title="Ana Sayfaya Git"
        >
          <img
            src="/operix-logo.png?v=8"
            alt="ACUTO 360"
            className="block h-auto w-full max-w-[220px] object-contain"
            draggable="false"
          />
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
            FUCAN PERSONEL GİYİM
          </div>
        </NavLink>
        <button
          data-testid="close-mobile-nav"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden text-slate-400 hover:text-slate-100 p-2 -mr-2"
          aria-label="Kapat"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 min-h-0 px-3 py-5 space-y-1 overflow-y-auto overscroll-contain" onScroll={closeGroups} style={{ WebkitOverflowScrolling: "touch" }}>
        {(() => {
          let lastSection = null;
          return visibleGroups.map((group) => {
            const sectionTitle = GROUP_SECTIONS[group.key] || null;
            const showSectionHeader = sectionTitle && sectionTitle !== lastSection;
            if (showSectionHeader) lastSection = sectionTitle;
            const GroupIcon = group.icon;
            const isOpen = group.single ? true : !!openGroups[group.key];
            const isActiveGroup = activeGroupKey === group.key;
            const sectionHeaderNode = showSectionHeader ? (
              <div key={`sec-${sectionTitle}`} className={lastSection === sectionTitle && sectionTitle === "ANA" ? "pt-1 pb-2" : "pt-4 pb-2"}>
                {sectionTitle !== "ANA" && (
                  <div className="mx-3 mb-2 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
                )}
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-mono px-3">
                  {sectionTitle}
                </div>
              </div>
            ) : null;

            // Tek elemanlı gruplar için başlığı doğrudan NavLink olarak render et
            if (group.single) {
              const only = group.items[0];
              const OnlyIcon = only.icon;
              return (
                <Fragment key={group.key}>
                  {sectionHeaderNode}
                  <NavLink
                    to={only.to}
                    end={only.exact}
                    data-testid={only.testId}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-semibold uppercase tracking-wide transition-colors ${
                        isActive
                          ? "bg-blue-600 text-white"
                          : "text-slate-300 hover:bg-slate-900"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={`w-7 h-7 flex items-center justify-center rounded-full ring-1 ${
                            isActive ? "ring-white/50 bg-white/10" : "ring-slate-700 bg-slate-950/50"
                          }`}
                        >
                          <OnlyIcon className="w-3.5 h-3.5" strokeWidth={2} />
                    </span>
                    <span className="flex-1 text-xs">{only.label}</span>
                  </>
                )}
              </NavLink>
                </Fragment>
              );
            }

          return (
            <Fragment key={group.key}>
              {sectionHeaderNode}
              <div className="pb-0.5">
              <button
                type="button"
                ref={(el) => { btnRefs.current[group.key] = el; }}
                data-testid={`nav-group-${group.key}`}
                onClick={() => toggleGroup(group.key)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-semibold uppercase tracking-wide transition-colors ${
                  isActiveGroup || isOpen
                    ? "text-slate-100 bg-slate-900/70"
                    : "text-slate-300 hover:bg-slate-900/60 hover:text-slate-100"
                }`}
              >
                <span
                  className="w-7 h-7 flex items-center justify-center rounded-full ring-1 ring-slate-700 bg-slate-950/50 shrink-0"
                >
                  <GroupIcon className="w-3.5 h-3.5" strokeWidth={2} />
                </span>
                <span className="flex-1 text-left text-xs">{group.label}</span>
                {group.badge && (
                  <span className="text-[9px] uppercase tracking-widest font-bold bg-emerald-500 text-emerald-950 px-1.5 py-0.5 rounded-sm">
                    {group.badge}
                  </span>
                )}
                {isOpen
                  ? <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  : <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
              </button>
              </div>
            </Fragment>
          );
        });
        })()}
      </nav>
      {/* Submenu overlay paneli — document flow DIŞI: ana menü blokları kaymaz.
          Butona demirli, max-height + internal scroll, backdrop ile kapanır. */}
      {openGroup && (() => {
        const anchor = btnRefs.current[openGroup.key];
        if (!anchor) return null;
        const rect = anchor.getBoundingClientRect();
        // Gizli instance (desktop aside mobilde gizli / mobil drawer desktop'ta
        // gizli) panel render etmesin — çift panel oluşmasın.
        if (rect.width === 0 && rect.height === 0) return null;
        const isDesktop = window.innerWidth >= 1024;
        let style;
        if (isDesktop) {
          // Desktop: flyout sidebar'ın SAĞINA açılır — diğer grup butonlarını
          // asla örtmez, tek tıkla grup değişimi mümkün kalır.
          const maxH = Math.max(160, Math.min(420, window.innerHeight - 24));
          style = {
            left: rect.right + 8,
            width: 264,
            top: Math.max(8, Math.min(rect.top, window.innerHeight - maxH - 8)),
            maxHeight: maxH,
          };
        } else {
          // Mobil/tablet: butonun altına (yer yoksa üstüne) demirlenir.
          const spaceBelow = window.innerHeight - rect.bottom - 12;
          const openUp = spaceBelow < 220 && rect.top > spaceBelow;
          const maxH = Math.max(160, Math.min(320, openUp ? rect.top - 12 : spaceBelow));
          style = openUp
            ? { left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top + 6, maxHeight: maxH }
            : { left: rect.left, width: rect.width, top: rect.bottom + 6, maxHeight: maxH };
        }
        return createPortal(
          <div
            data-testid={`nav-group-panel-${openGroup.key}`}
            className="fixed z-[70] overflow-y-auto overscroll-contain rounded-sm bg-slate-900 border border-slate-800 p-1.5 space-y-0.5 shadow-2xl shadow-black/60"
            style={style}
          >
            {renderGroupItems(openGroup)}
          </div>,
          document.body
        );
      })()}
    </>
    );
  };

  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* Desktop sidebar */}
      <aside className="app-sidebar hidden lg:flex w-64 bg-slate-950 border-r border-slate-800 flex-col sticky top-0 h-screen shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile off-canvas sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="app-sidebar relative w-72 max-w-[85vw] h-full max-h-full bg-slate-950 border-r border-slate-800 flex flex-col">
            <SidebarContent btnRefs={mobileBtnRefs} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main + mobile top bar */}
      <main className="flex-1 min-w-0 pb-16 lg:pb-0">
        {/* Desktop top bar with notification bell */}
        <div className="hidden lg:flex sticky top-0 z-30 bg-slate-950/95 backdrop-blur border-b border-slate-800 items-center justify-end gap-2 px-6 py-2">
          <button
            data-testid="topbar-change-pw"
            onClick={() => setChangePwOpen(true)}
            className="text-xs uppercase tracking-widest font-mono text-slate-400 hover:text-blue-400 px-3 py-1.5 border border-slate-800 rounded-sm inline-flex items-center gap-1.5"
            title="Şifre Değiştir"
          >
            <KeyRound className="w-3.5 h-3.5" /> Şifre
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="topbar-account-btn"
                className="text-xs uppercase tracking-widest font-mono text-slate-400 hover:text-blue-400 px-3 py-1.5 border border-slate-800 rounded-sm inline-flex items-center gap-1.5"
                title={user?.email || "Hesap"}
              >
                <User className="w-3.5 h-3.5" />
                <span className="max-w-[120px] truncate normal-case tracking-normal">{user?.name || "Hesap"}</span>
                <ChevronDown className="w-3 h-3 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <AccountDropdownContent
              user={user}
              onAccount={() => setAccountOpen(true)}
              onChangePw={() => setChangePwOpen(true)}
              onForceUpdate={forceUpdate}
              onLogout={handleLogout}
            />
          </DropdownMenu>
          <HelpButton />
          <NotificationBell />
        </div>
        {/* Mobile-only top bar */}
        <div className="lg:hidden sticky top-0 z-30 bg-slate-950/95 backdrop-blur border-b border-slate-800 flex items-center gap-2 px-3 sm:px-4 py-3" data-testid="mobile-topbar">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
            className="text-slate-300 hover:text-blue-500 p-1 -ml-1 shrink-0"
            aria-label="Menü"
          >
            <Menu className="w-6 h-6" />
          </button>
          <NavLink to="/" data-testid="mobile-home-link" className="flex items-center gap-3 min-w-0 flex-1 hover:opacity-80">
            <img
              src="/operix-icon.png?v=8"
              alt="ACUTO 360"
              className="h-9 w-9 object-contain shrink-0"
              draggable="false"
            />
            <div className="min-w-0 ml-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono truncate">FUCAN PERSONEL GİYİM</div>
              <div className="text-sm font-bold text-slate-50 tracking-tight truncate">{currentLabel}</div>
            </div>
          </NavLink>
          <div
            className="shrink-0 flex items-center gap-1"
            style={{ display: "flex", flexShrink: 0 }}
            data-testid="mobile-topbar-actions"
          >
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="mobile-account-btn"
                  className="relative shrink-0 text-slate-300 hover:text-blue-400 p-2 rounded-sm hover:bg-slate-800/60 transition-colors"
                  style={{ display: "inline-flex", flexShrink: 0, minWidth: 40, minHeight: 40 }}
                  aria-label="Hesap menüsü"
                  title={user?.email || "Hesap"}
                >
                  <UserCircle2 className="w-6 h-6" strokeWidth={2} />
                </button>
              </DropdownMenuTrigger>
              <AccountDropdownContent
                user={user}
                onAccount={() => setAccountOpen(true)}
                onChangePw={() => setChangePwOpen(true)}
                onForceUpdate={forceUpdate}
                onLogout={handleLogout}
              />
            </DropdownMenu>
          </div>
        </div>

        <Outlet />
      </main>

      {/* Marka altbilgi — OPERIX AI ERP */}
      <div className="hidden lg:block fixed bottom-0 left-64 right-0 z-10 pointer-events-none">
        <div className="px-6 py-2 flex items-center justify-between text-[10px] uppercase tracking-widest font-mono text-slate-500 border-t border-slate-800 bg-slate-950/70 backdrop-blur">
          <span className="text-slate-400">
            <span className="font-bold text-blue-400">ACUTO 360</span>
            <span className="mx-2 text-slate-700">|</span>
            <span>Daha Verimli Yarınlar</span>
          </span>
          <span className="text-slate-600">Sürüm 1.0.0</span>
        </div>
      </div>

      {/* WhatsNew "Yeni Özellik" bildirimi */}
      <WhatsNewBanner />

      {/* Mobile-only floating quick actions */}
      <MobileQuickActions />

      {/* AI Copilot — visible on all viewports */}
      <CopilotWidget />

      {/* Şifre Değiştir Diyaloğu */}
      <ChangePasswordDialog
        open={changePwOpen}
        onOpenChange={setChangePwOpen}
        forced={!!user?.force_password_change}
        onChanged={() => refresh()}
      />

      {/* Hesap Bilgileri Diyaloğu */}
      <AccountInfoDialog open={accountOpen} onOpenChange={setAccountOpen} user={user} />

      {/* AI Ürün Oluşturucu — Copilot komutu veya Ürünler butonuyla açılır */}
      <AiProductDraftDialog
        open={aiProductOpen}
        onClose={() => setAiProductOpen(false)}
        onCreated={() => {
          setAiProductOpen(false);
          // Ürünler sayfası açıksa listeyi yenile, değilse Ürünler'e yönlendir
          if (location?.pathname === "/urunler") {
            window.dispatchEvent(new CustomEvent("fucan:products-refresh"));
          } else {
            navigate("/urunler");
          }
        }}
        initialPrompt={aiProductPrompt}
      />
    </div>
  );
}

// -------- Ortak profil dropdown içeriği (Desktop + Mobile) --------
const ROLE_LABELS = {
  admin: "Yönetici",
  sistem_yoneticisi: "Sistem Yöneticisi",
  member: "Kullanıcı",
  operator: "Operatör",
  muhasebe: "Muhasebe",
  satis: "Satış",
  uretim: "Üretim",
  depo: "Depo",
};

function humanizeRole(user) {
  if (!user) return "-";
  if (user.is_super_admin) return "Süper Yönetici";
  const key = (user.role_code || user.role || "").toLowerCase();
  return ROLE_LABELS[key] || (user.role_code || user.role || "-");
}

function AccountDropdownContent({ user, onAccount, onChangePw, onForceUpdate, onLogout }) {
  return (
    <DropdownMenuContent
      align="end"
      className="bg-slate-900 border-slate-800 text-slate-200 rounded-sm w-72"
      data-testid="account-dropdown"
    >
      <div className="px-3 py-2.5 border-b border-slate-800">
        <div className="text-[10px] uppercase text-slate-500 font-mono">Oturum</div>
        <div className="text-sm text-slate-100 font-medium truncate mt-0.5" data-testid="current-user-name">
          {user?.name || "-"}
        </div>
        <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-950/60 text-blue-300 border border-blue-900/60 uppercase tracking-wider" data-testid="user-role-badge">
            {humanizeRole(user)}
          </span>
          {user?.company_name && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-800 text-slate-300 border border-slate-700 truncate max-w-[180px]" data-testid="user-company-badge" title={user.company_name}>
              {user.company_name}
            </span>
          )}
        </div>
      </div>
      <DropdownMenuItem onClick={onAccount} className="text-xs cursor-pointer" data-testid="menu-account-btn">
        <User className="w-3.5 h-3.5 mr-2" /> Hesabım
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onChangePw} className="text-xs cursor-pointer" data-testid="menu-change-password-btn">
        <KeyRound className="w-3.5 h-3.5 mr-2" /> Şifre Değiştir
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onForceUpdate} className="text-xs cursor-pointer text-emerald-400 focus:text-emerald-300" data-testid="force-update-btn">
        <RefreshCw className="w-3.5 h-3.5 mr-2" /> Uygulamayı Güncelle
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onLogout} className="text-xs cursor-pointer text-rose-400 focus:text-rose-300" data-testid="logout-btn">
        <LogOut className="w-3.5 h-3.5 mr-2" /> Çıkış Yap
      </DropdownMenuItem>
      <div className="text-[10px] text-slate-600 font-mono text-center px-3 py-1 border-t border-slate-800" data-testid="app-version">
        Sürüm v24 · 2026-08-31
      </div>
    </DropdownMenuContent>
  );
}
